import { Box, Flex, Text, VStack } from "@chakra-ui/react";
import { CloseIcon } from "@chakra-ui/icons";
import { useTranslation } from "react-i18next";
import { useReadingDirection } from "../../hooks/useReadingDirection";
import { usePageLayout } from "../../hooks/usePageLayout";
import { PAGE_LAYOUT_TYPES, READING_DIRECTION_TYPES } from "../../constants";
import { useP2PSettings } from "../../hooks/useP2PSettings";
import { DirectionIndicator } from "../DirectionIndicator";
import { CircleArrowLeftIcon } from "../../../../components/icons/CircleArrowLeftIcon";
import { CircleArrowRightIcon } from "../../../../components/icons/CircleArrowRightIcon";
import { MenuHintIcon } from "../../../../components/icons/MenuHintIcon";
import { useIsSmallBreakpoint } from "../../../../utils/useBreakpoints";
import { MenuHintThinIcon } from "../../../../components/icons/MenuHintThinIcon";

// Constants
const NAVIGATION_WIDTH = "35%";
const CENTER_WIDTH = "30%";
const ICON_SIZE = "40px";
const FONT_SIZE = "18px";
const FONT_WEIGHT = "700";

const BORDER_GRADIENT_STYLE = {
  content: '""',
  position: 'absolute' as const,
  top: 0,
  width: '2px',
  height: '100%',
  background: 'linear-gradient(to bottom, transparent 0px, rgba(255, 255, 255, 0.2) 20px, rgba(255, 255, 255, 0.2) calc(100% - 20px), transparent 100%)',
  maskImage: 'repeating-linear-gradient(0deg, transparent, transparent 3px, white 3px, white 9px)',
  WebkitMaskImage: 'repeating-linear-gradient(0deg, transparent, transparent 3px, white 3px, white 9px)'
};

interface ReaderHintsProps {
  isVisible: boolean;
  onInteraction: () => void;
}

export function ReaderHints({ isVisible, onInteraction }: ReaderHintsProps) {
  const { t } = useTranslation();
  const { readingDirection, comicIsVertical } = useReadingDirection();
  const isSmallBreakpoint = useIsSmallBreakpoint();
  const { pageLayout } = usePageLayout();
  const { p2pEnabledAndAugmented, p2pEnabled } = useP2PSettings();

  // Get current reading mode text (P2P takes priority over other modes)
  const getReadingModeText = () => {
    if (p2pEnabledAndAugmented) {
      return t("screens.reader.hints.readingMode.panelToPanel");
    }
    if (comicIsVertical || pageLayout === PAGE_LAYOUT_TYPES.vertical) {
      return t("screens.reader.hints.readingMode.vertical");
    }
    if (pageLayout === PAGE_LAYOUT_TYPES.double) {
      return t("screens.reader.hints.readingMode.doublePage");
    }
    return t("screens.reader.hints.readingMode.singlePage");
  };

  return (
    <Box
      position="fixed"
      top={0}
      left={0}
      right={0}
      bottom={0}
      bg="rgba(0, 0, 0, 0.8)"
      zIndex={10}
      opacity={isVisible ? 1 : 0}
      transition="opacity 0.3s ease-in-out"
      onClick={onInteraction}
      pointerEvents={isVisible ? "auto" : "none"}
      backdropFilter={"blur(4px)"}
    >
      {(p2pEnabled ||
        readingDirection !== READING_DIRECTION_TYPES.vertical) && (
        <>
          <Box
            position="absolute"
            top={0}
            bottom={0}
            left={0}
            width={NAVIGATION_WIDTH}
            _after={{
              ...BORDER_GRADIENT_STYLE,
              right: 0,
            }}
            display="flex"
            alignItems="center"
            justifyContent="center"
            onClick={(e) => {
              e.stopPropagation();
              onInteraction();
            }}
          >
            <VStack spacing={2}>
              <CircleArrowLeftIcon
                width={ICON_SIZE}
                height={ICON_SIZE}
                color="success.300"
              />
              <Text
                fontSize={FONT_SIZE}
                fontWeight={FONT_WEIGHT}
                textAlign="center"
                color="dune.dune"
              >
                {readingDirection === READING_DIRECTION_TYPES.rightToLeft
                  ? t("screens.reader.hints.navigation.next")
                  : t("screens.reader.hints.navigation.previous")}
              </Text>
            </VStack>
          </Box>

          <Box
            position="absolute"
            top={0}
            bottom={0}
            right={0}
            width={NAVIGATION_WIDTH}
            _after={{
              ...BORDER_GRADIENT_STYLE,
              left: 0,
            }}
            display="flex"
            alignItems="center"
            justifyContent="center"
            onClick={(e) => {
              e.stopPropagation();
              onInteraction();
            }}
          >
            <VStack spacing={2}>
              <CircleArrowRightIcon
                width={ICON_SIZE}
                height={ICON_SIZE}
                color="success.300"
              />
              <Text
                fontSize={FONT_SIZE}
                fontWeight={FONT_WEIGHT}
                textAlign="center"
                color="dune.dune"
              >
                {readingDirection === READING_DIRECTION_TYPES.rightToLeft
                  ? t("screens.reader.hints.navigation.previous")
                  : t("screens.reader.hints.navigation.next")}
              </Text>
            </VStack>
          </Box>
        </>
      )}

      <Box
        position="absolute"
        top={0}
        bottom={0}
        left={
          readingDirection === READING_DIRECTION_TYPES.vertical ||
          p2pEnabledAndAugmented
            ? 0
            : CENTER_WIDTH
        }
        right={
          readingDirection === READING_DIRECTION_TYPES.vertical ||
          p2pEnabledAndAugmented
            ? 0
            : CENTER_WIDTH
        }
        display="flex"
        alignItems="center"
        justifyContent="center"
        onClick={(e) => {
          e.stopPropagation();
          onInteraction();
        }}
      >
        <VStack spacing={2}>
          {isSmallBreakpoint ? (
            <MenuHintThinIcon width="40px" height="40px" color="blaze.300" />
          ) : (
            <MenuHintIcon width="40px" height="40px" color="blaze.300" />
          )}
          <Text
            fontSize="18px"
            fontWeight="700"
            textAlign="center"
            color="dune.dune"
          >
            {t("screens.reader.hints.navigation.menu")}
          </Text>
        </VStack>
      </Box>

      <Flex
        position="absolute"
        top="100px"
        left="50%"
        transform="translateX(-50%)"
        gap="16px"
        flexDirection="column"
        alignItems="center"
      >
        <Text
          fontSize="18px"
          fontWeight="700"
          textAlign="center"
          color="dune.dune"
        >
          {getReadingModeText()}
        </Text>
        <DirectionIndicator readingDirection={readingDirection} />
      </Flex>

      <Box
        position="absolute"
        cursor="pointer"
        top="40px"
        right={isSmallBreakpoint ? "24px" : "40px"}
        onClick={(e) => {
          e.stopPropagation();
          onInteraction();
        }}
      >
        <CloseIcon width="24px" height="24px" color="white" />
      </Box>
    </Box>
  );
}
